import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { FormControlLabel, Radio } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import {
	Menu,
	MenuItemContentWrapper,
	MenuItemListWrapper,
	MenuItemWrapper,
	StyledChip
} from "components/OrderSearch/chipDropdownMenu.styles.ts";
import * as Text from "../../LeadAssignmentsPage.text.ts";
import { LeadDateFilterOption } from "../../utils";

interface LeadAssignmentsDateFilterProps {
	selectedDateFilter: LeadDateFilterOption;
	onChange: (value: LeadDateFilterOption) => void;
}

const dateFilterOptions: { label: string; value: Exclude<LeadDateFilterOption, "all"> }[] = [
	{ label: Text.Last7DaysText, value: "last7Days" },
	{ label: Text.Last30DaysText, value: "last30Days" },
	{ label: Text.Last365DaysText, value: "last365Days" }
];

export const LeadAssignmentsDateFilter = ({ selectedDateFilter, onChange }: LeadAssignmentsDateFilterProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);
	const hasSelection = selectedDateFilter !== "all";

	const chipLabel = useMemo(() => {
		if (selectedDateFilter === "all") {
			return Text.DateFilterLabel;
		}

		return dateFilterOptions.find((option) => option.value === selectedDateFilter)?.label ?? Text.DateFilterLabel;
	}, [selectedDateFilter]);

	const handleClear = () => {
		onChange("all");
		setIsOpen(false);
	};

	return (
		<div>
			<StyledChip
				hasWarning={false}
				clickable
				ref={anchorRef}
				variant="outlined"
				label={chipLabel}
				onClick={() => setIsOpen((prev) => !prev)}
				icon={<ExpandMoreRounded />}
				color={hasSelection ? "secondary" : "primary"}
				data-testid="lead-assignments-date-filter-chip"
				deleteIcon={
					hasSelection ? (
						<div data-testid="lead-assignments-date-filter-clear">
							<CancelIcon />
						</div>
					) : undefined
				}
				onDelete={hasSelection ? handleClear : undefined}
			/>
			<Menu
				open={isOpen}
				anchorEl={anchorRef.current}
				onClose={() => setIsOpen(false)}
				data-testid="lead-assignments-date-filter-menu"
			>
				<MenuItemListWrapper
					isSearchable={false}
					child={undefined}
				>
					{dateFilterOptions.map((option) => (
						<MenuItemWrapper key={option.value}>
							<MenuItemContentWrapper item={option.label}>
								<FormControlLabel
									control={
										<Radio
											size="medium"
											checked={selectedDateFilter === option.value}
											onChange={() => {
												onChange(option.value);
												setIsOpen(false);
											}}
										/>
									}
									label={option.label}
								/>
							</MenuItemContentWrapper>
						</MenuItemWrapper>
					))}
				</MenuItemListWrapper>
			</Menu>
		</div>
	);
};

export default LeadAssignmentsDateFilter;
